// Dependency-free SVG radar for the crypto-specific dimension breakdown (SPEC §6.2).
export function Radar({ data }: { data: { label: string; avg: number }[] }) {
  const n = data.length;
  if (n < 3) return <DimensionBars data={data} />;

  const size = 260;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 40;
  const max = 5;

  const point = (i: number, value: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const rad = (value / max) * r;
    return [cx + rad * Math.cos(angle), cy + rad * Math.sin(angle)] as const;
  };

  const rings = [1, 2, 3, 4, 5];
  const areaPath = data.map((d, i) => point(i, d.avg)).map(([x, y], i) => `${i ? "L" : "M"}${x},${y}`).join(" ") + " Z";

  return (
    <svg className="radar" viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="Dimension ratings radar">
      {rings.map((ring) => (
        <polygon
          key={ring}
          className="grid-line"
          points={data.map((_, i) => point(i, ring).join(",")).join(" ")}
        />
      ))}
      {data.map((_, i) => {
        const [x, y] = point(i, max);
        return <line key={i} className="axis" x1={cx} y1={cy} x2={x} y2={y} />;
      })}
      <path className="area" d={areaPath} />
      {data.map((d, i) => {
        const [x, y] = point(i, max + 0.55);
        const anchor = Math.abs(x - cx) < 8 ? "middle" : x < cx ? "end" : "start";
        return (
          <text key={i} className="label" x={x} y={y} textAnchor={anchor} dominantBaseline="middle">
            {d.label.split(" ")[0]}
          </text>
        );
      })}
    </svg>
  );
}

export function DimensionBars({ data }: { data: { label: string; avg: number; count?: number }[] }) {
  return (
    <div className="grid u-f3d7ce7">
      {data.map((d) => (
        <div key={d.label}>
          <div className="row u-cf4b235">
            <span className="u-370c861">{d.label}</span>
            <span className="metric muted u-481a3cd">{d.avg ? d.avg.toFixed(1) : "—"}</span>
          </div>
          {/* SVG geometry, not CSS. A data-driven width has to come from
              somewhere, and an SVG width attribute is not a style so this is
              the one shape that does not force 'unsafe-inline' into style-src. */}
          <svg
            className="bar"
            viewBox="0 0 100 6"
            preserveAspectRatio="none"
            role="img"
            aria-label={`${d.label}: ${d.avg ? d.avg.toFixed(1) : "no"} out of 5`}
          >
            <rect className="bar-track" x="0" y="0" width="100" height="6" rx="3" />
            <rect className="bar-fill" x="0" y="0" width={(d.avg / 5) * 100} height="6" rx="3" />
          </svg>
        </div>
      ))}
    </div>
  );
}
